import React, { useEffect, useState } from 'react'
import { Card, Form } from 'react-bootstrap-v5'
import Select from 'react-select'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { getRequestOptions } from '../helpers/Fetchwrapper'
import { PlanningFilters, horizonOptions, methodOptions, toInputDate, toNumber } from '../helpers/PlanningHelper'
import GetPagePermission from '../components/GetPagePermission'
import PlanningStockCover from '../components/PlanningStockCover'
import PlanningDemandForecast from '../components/PlanningDemandForecast'
import PlanningProductionPlan from '../components/PlanningProductionPlan'
import PlanningMaterialRequirement from '../components/PlanningMaterialRequirement'
import PlanningForecastAccuracy from '../components/PlanningForecastAccuracy'

const tabs = [
	{ key: 'cover', label: 'Stock Cover', component: PlanningStockCover },
	{ key: 'demand', label: 'Demand Forecast', component: PlanningDemandForecast },
	{ key: 'plan', label: 'Production Plan', component: PlanningProductionPlan },
	{ key: 'materials', label: 'Material Requirement', component: PlanningMaterialRequirement },
	{ key: 'accuracy', label: 'Forecast vs Actual', component: PlanningForecastAccuracy },
]

const defaultFilters: PlanningFilters = {
	idProjects: '',
	asOfDate: toInputDate(new Date()),
	method: 3,
	horizon: 30,
	safetyDays: 7,
	pendingDays: 90,
	poDays: 180,
}

const ProductionPlanning: React.FC = () => {
	const hasPermission = GetPagePermission('/production-planning')

	const [projects, setProjects] = useState<{ label: string; value: number }[]>([])
	const [form, setForm] = useState<PlanningFilters>(defaultFilters)
	// what the reports are showing: the form as it was when "View" was last clicked
	const [filters, setFilters] = useState<PlanningFilters>(defaultFilters)
	const [activeTab, setActiveTab] = useState(tabs[0].key)
	// a tab is built the first time it is opened and kept, so figures typed in it survive a look at another tab
	const [openedTabs, setOpenedTabs] = useState<string[]>([tabs[0].key])

	useEffect(() => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options = (resp.data || []).map((row: any) => ({ label: row.name, value: row.id }))
				setProjects(options)
				if (options.length > 0) {
					setForm((current) => ({ ...current, idProjects: options[0].value }))
					setFilters((current) => ({ ...current, idProjects: options[0].value }))
				}
			})
			.catch((error) => {
				console.log(error, 'catch the hoop')
			})
	}, [])

	const openTab = (key: string) => {
		setActiveTab(key)
		if (!openedTabs.includes(key)) {
			setOpenedTabs(openedTabs.concat(key))
		}
	}

	const setDays = (name: 'safetyDays' | 'pendingDays' | 'poDays', value: string) => {
		setForm({ ...form, [name]: Math.max(0, Math.round(toNumber(value))) })
	}

	if (hasPermission !== 1) {
		return null
	}

	return (
		<>
			<Card className='mb-4'>
				<Card.Body>
					<Card.Title className='h5 mb-1'>Production Planning</Card.Title>
					<p className='text-muted small mb-3'>
						How long stock will last, what is likely to sell, what to make and which materials to buy. Set the figures below and click "View"; they apply to every tab.
					</p>
					<div className='row align-items-end'>
						<div className='col-md-3'>
							<Form.Group className='mb-3' controlId='planningProject'>
								<Form.Label>Project<span className='required'></span></Form.Label>
								<Select
									value={projects.find((project) => project.value === form.idProjects) || null}
									onChange={(selected: any) => setForm({ ...form, idProjects: selected.value })}
									options={projects}
									menuPosition='fixed'
								/>
							</Form.Group>
						</div>
						<div className='col-md-2'>
							<Form.Group className='mb-3' controlId='planningAsOf'>
								<Form.Label>Stock As Of</Form.Label>
								<Form.Control type='date' value={form.asOfDate} max={toInputDate(new Date())} onChange={(event) => setForm({ ...form, asOfDate: event.target.value })} />
							</Form.Group>
						</div>
						<div className='col-md-3'>
							<Form.Group className='mb-3' controlId='planningMethod'>
								<Form.Label>Forecast From</Form.Label>
								<Form.Select id='planningMethod' value={form.method} onChange={(event) => setForm({ ...form, method: parseInt(event.currentTarget.value, 10) })}>
									{methodOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
								</Form.Select>
							</Form.Group>
						</div>
						<div className='col-md-2'>
							<Form.Group className='mb-3' controlId='planningHorizon'>
								<Form.Label>Plan For</Form.Label>
								<Form.Select id='planningHorizon' value={form.horizon} onChange={(event) => setForm({ ...form, horizon: parseInt(event.currentTarget.value, 10) })}>
									{horizonOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
								</Form.Select>
							</Form.Group>
						</div>
						<div className='col-md-2'>
							<div className='mb-3'>
								<button className='btn btn-sm btn-primary p-3 w-100' onClick={() => setFilters({ ...form, asOfDate: form.asOfDate || toInputDate(new Date()) })} disabled={!form.idProjects}>
									<i className='fas fa-eye me-1'></i>View
								</button>
							</div>
						</div>
					</div>
					<div className='row align-items-end'>
						<div className='col-md-3'>
							<Form.Group className='mb-0' controlId='planningSafetyDays'>
								<Form.Label className='small text-muted'>Safety stock, in days of forecast</Form.Label>
								<Form.Control size='sm' type='number' min='0' max='90' value={form.safetyDays} onChange={(event) => setDays('safetyDays', event.target.value)} />
							</Form.Group>
						</div>
						<div className='col-md-4'>
							<Form.Group className='mb-0' controlId='planningPendingDays'>
								<Form.Label className='small text-muted'>Count orders to deliver from the last (days, 0 = all)</Form.Label>
								<Form.Control size='sm' type='number' min='0' value={form.pendingDays} onChange={(event) => setDays('pendingDays', event.target.value)} />
							</Form.Group>
						</div>
						<div className='col-md-4'>
							<Form.Group className='mb-0' controlId='planningPoDays'>
								<Form.Label className='small text-muted'>Count open POs from the last (days, 0 = all)</Form.Label>
								<Form.Control size='sm' type='number' min='0' value={form.poDays} onChange={(event) => setDays('poDays', event.target.value)} />
							</Form.Group>
						</div>
					</div>
				</Card.Body>
			</Card>

			<Card>
				<Card.Body>
					<ul className='nav nav-tabs nav-line-tabs mb-5 fs-6'>
						{tabs.map((tab) => (
							<li className='nav-item' key={tab.key}>
								<button type='button' className={`nav-link${activeTab === tab.key ? ' active' : ''}`} onClick={() => openTab(tab.key)}>
									{tab.label}
								</button>
							</li>
						))}
					</ul>

					{tabs.filter((tab) => openedTabs.includes(tab.key)).map((tab) => {
						const TabComponent = tab.component
						return (
							<div key={tab.key} style={{ display: activeTab === tab.key ? 'block' : 'none' }}>
								<TabComponent active={activeTab === tab.key} filters={filters} />
							</div>
						)
					})}
				</Card.Body>
			</Card>
			<ToastContainer />
		</>
	)
}

export default ProductionPlanning
